import { useEffect } from 'react';
import styled from '@emotion/styled';
import { mediaQuery } from '@styles/index';
import LoginFlow from '@cont/Login/LoginFlow';
import { campaignImage } from './shared';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 12000;
  background: rgba(7, 11, 24, 0.72);

  ${mediaQuery.uptoMobile} {
    background: linear-gradient(147.52deg, #07112e 57.71%, #510f1b 129.86%);
  }
`;

const Panel = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  width: 579px;
  height: 100%;
  background: linear-gradient(141.54deg, #07112e 57.75%, #510f1b 107.2%);

  ${mediaQuery.uptoMobile} {
    position: relative;
    width: 100%;
    background: none;
  }
`;

const Frame = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 464px;
  max-width: calc(100% - 32px);
  margin: 56px auto 0;
  gap: 40px;

  ${mediaQuery.uptoMobile} {
    width: 327px;
    margin: 48px auto 0;
    gap: 16px;
  }
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
`;

const Footer = styled.p`
  margin: auto 0 25px 53px;
  width: 405px;
  color: #a2a0a2;
  font-size: 12px;
  line-height: 18px;

  a {
    color: #fff;
    font-weight: 400;
    text-decoration: none;
  }

  ${mediaQuery.uptoMobile} {
    margin: 16px auto 32px;
    width: 327px;
    max-width: calc(100% - 32px);

    a {
      font-weight: 700;
    }
  }
`;

const DesktopHelp = styled.span`
  ${mediaQuery.uptoMobile} {
    display: none;
  }
`;

const MobileHelp = styled.span`
  display: none;

  ${mediaQuery.uptoMobile} {
    display: inline;
  }
`;

type CampaignLoginDrawerProps = {
  open: boolean;
  onClose: () => void;
  redirectPath?: string;
  onSuccess?: () => void;
};

const CampaignLoginDrawer = ({
  open,
  onClose,
  redirectPath = '',
  onSuccess
}: CampaignLoginDrawerProps) => {
  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <Overlay role="dialog" aria-modal="true" aria-label="Login">
      <Panel>
        <Frame>
          <CloseButton type="button" onClick={onClose} aria-label="Close">
            <img src={campaignImage('login-close.svg')} alt="" />
          </CloseButton>
          <LoginFlow
            source="drawer"
            hideHelp
            campaignTheme
            countrySelected={null}
            presetRedirectURL={redirectPath}
            loginCallback={onSuccess}
          />
        </Frame>
        <Footer>
          <DesktopHelp>
            Having trouble? Please contact{' '}
            <a href="mailto:help@unacademy.com">help@unacademy.com</a> for
            further support
          </DesktopHelp>
          <MobileHelp>
            Having trouble? Visit our{' '}
            <a href="https://unacademy.com/helpcenter">Help center</a>
          </MobileHelp>
        </Footer>
      </Panel>
    </Overlay>
  );
};

export default CampaignLoginDrawer;
